import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import axios from "axios"
import AppLayout from "@/components/app/AppLayout"
import { PilotBanner } from "@/components/app/PilotBanner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { API_BASE } from "@/config/api"
import { HrSubnav } from "./HrSubnav"
import { toast } from "sonner"

const API = API_BASE

export default function HrContracts() {
  const { t } = useTranslation()
  const [templates, setTemplates] = useState([])
  const [employees, setEmployees] = useState([])
  const [contracts, setContracts] = useState([])
  const [form, setForm] = useState({
    employee_id: "",
    template_id: "cim_part_time_generala",
    start_date: new Date().toISOString().slice(0, 10),
    gross_salary: "",
  })

  const load = () => {
    axios.get(`${API}/hr/contracts`).then((r) => setContracts(r.data)).catch(() => {})
  }

  useEffect(() => {
    axios.get(`${API}/hr/contract-templates`).then((r) => setTemplates(r.data)).catch(() => {})
    axios.get(`${API}/hr/employees`).then((r) => setEmployees(r.data)).catch(() => {})
    load()
  }, [])

  const createContract = async () => {
    if (!form.employee_id || !form.start_date) return toast.error("Angajat și dată start obligatorii")
    try {
      await axios.post(`${API}/hr/contracts`, {
        employee_id: form.employee_id,
        template_id: form.template_id,
        start_date: form.start_date,
        gross_salary: Number(form.gross_salary) || 0,
      })
      load()
      toast.success("Contract draft creat")
    } catch {
      toast.error("Eșuat")
    }
  }

  const generatePdf = async (contractId) => {
    try {
      const { data: pdf } = await axios.post(`${API}/hr/contracts/${contractId}/generate`)
      if (pdf.pdf_base64) {
        const a = document.createElement("a")
        a.href = `data:application/pdf;base64,${pdf.pdf_base64}`
        a.download = `CIM-${contractId.slice(0, 8)}.pdf`
        a.click()
      }
      load()
      toast.success("PDF generat")
    } catch {
      toast.error("Eșuat")
    }
  }

  return (
    <AppLayout title={t("app.hr.contracts.title")}>
      <HrSubnav />
      <PilotBanner className="mb-4 max-w-2xl">
        Șabloane orientative — verificare juridică obligatorie înainte de semnare. DocuSign/certSIGN: contactați suportul pentru activare.
      </PilotBanner>
      <div className="grid lg:grid-cols-2 gap-8 max-w-5xl">
        <section className="rounded-xl border border-border p-6 bg-card space-y-3">
          <h3 className="text-sm font-medium">Contract nou</h3>
          <div>
            <Label className="text-xs">Angajat</Label>
            <Select value={form.employee_id} onValueChange={(v) => setForm((f) => ({ ...f, employee_id: v }))}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Alege" /></SelectTrigger>
              <SelectContent>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Șablon</Label>
            <Select value={form.template_id} onValueChange={(v) => setForm((f) => ({ ...f, template_id: v }))}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {templates.map((tpl) => (
                  <SelectItem key={tpl.id} value={tpl.id}>{tpl.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Data start</Label>
            <Input type="date" className="mt-1" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">Salariu brut (RON)</Label>
            <Input type="number" className="mt-1" value={form.gross_salary} onChange={(e) => setForm((f) => ({ ...f, gross_salary: e.target.value }))} />
          </div>
          <Button className="rounded-full" onClick={createContract}>Creează draft</Button>
        </section>
        <section>
          <h3 className="text-sm font-medium mb-3">Contracte</h3>
          <ul className="space-y-2 text-sm">
            {contracts.map((c) => (
              <li key={c.id} className="rounded-xl border border-border p-4 flex justify-between items-center gap-2 flex-wrap">
                <div>
                  <p className="font-medium">{c.employee_name}</p>
                  <p className="text-xs text-muted-foreground">{c.contract_number} · {c.status}</p>
                </div>
                {c.status === "draft" && (
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => generatePdf(c.id)}>
                    Generează PDF
                  </Button>
                )}
                {c.status === "generated" && (
                  <span className="text-xs text-muted-foreground">PDF disponibil</span>
                )}
              </li>
            ))}
            {!contracts.length && <p className="text-muted-foreground text-sm">Niciun contract.</p>}
          </ul>
        </section>
      </div>
      <h3 className="text-sm font-medium mt-8 mb-3">Șabloane disponibile</h3>
      <ul className="space-y-2 max-w-xl">
        {templates.map((tpl) => (
          <li key={tpl.id} className="rounded-lg border border-border px-4 py-2 text-sm">
            <span className="font-medium">{tpl.label}</span>
            <span className="text-muted-foreground text-xs ml-2">{tpl.kind}</span>
          </li>
        ))}
      </ul>
    </AppLayout>
  )
}
